import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  Package, 
  MapPin, 
  FileText, 
  Users, 
  Wallet, 
  TrendingUp,
  Plus,
  ArrowRight
} from 'lucide-react';
import { dashboardAPI, walletAPI, authAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import SEO from '../components/SEO';

const StatCard = ({ title, value, icon: Icon, color, link }) => {
  const colors = useThemeColors();
  
  return (
    <Link to={link} className="block">
      <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden group">
        {/* Top accent bar */}
        <div className={`h-1 ${color}`} />
        
        {/* Content */}
        <div className="p-6">
          <div className="flex items-center">
            <div className={`p-3 rounded-xl ${color} group-hover:scale-110 transition-transform duration-300`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-theme-secondary">{title}</p>
              <p className="text-2xl font-bold text-theme-primary">{value}</p>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const QuickAction = ({ title, description, icon: Icon, link, color }) => {
  const colors = useThemeColors();
  
  return (
    <Link to={link} className="block">
      <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden group">
        {/* Top accent bar */}
        <div className={`h-1 ${color}`} />
        
        {/* Content */}
        <div className="p-6">
          <div className="flex items-start">
            <div className={`p-3 rounded-xl ${color} group-hover:scale-110 transition-transform duration-300`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4 flex-1">
              <h3 className="text-lg font-semibold text-theme-primary">{title}</h3>
              <p className="text-sm text-theme-secondary mt-1">{description}</p>
              <div className="flex items-center mt-3 text-theme-primary text-sm font-medium group-hover:text-theme-primary-hover transition-colors">
                Get started
                <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const Dashboard = () => {
  const { data: dashboardData, isLoading } = useQuery('dashboard', dashboardAPI.getDashboardData, {
    select: (response) => {
      console.log(response?.data?.data);
      return response?.data?.data;
    },
  });
  const stats = [
    {
      title: 'My Parcels',
      value: dashboardData?.stats?.parcels_count || 0,
      icon: Package,
      color: 'bg-theme-primary',
      link: '/parcels'
    },
    {
      title: 'My Trips',
      value: dashboardData?.stats?.trips_count || 0,
      icon: MapPin,
      color: 'bg-theme-secondary',
      link: '/trips'
    },
    {
      title: 'Applications',
      value: dashboardData?.stats?.applications_count || 0,
      icon: FileText,
      color: 'bg-theme-warning',
      link: '/applications'
    },
    {
      title: 'Active Agreements',
      value: dashboardData?.stats?.active_agreements_count || 0,
      icon: Users,
      color: 'bg-theme-error',
      link: '/agreements'
    },
  ];

  const quickActions = [
    {
      title: 'Post a Parcel',
      description: 'Send a parcel with a traveller',
      icon: Package,
      link: '/parcels?action=new',
      color: 'bg-theme-primary'
    },
    {
      title: 'Post a Trip',
      description: 'Offer to carry parcels on your trip',
      icon: MapPin,
      link: '/trips?action=new',
      color: 'bg-theme-secondary'
    },
    {
      title: 'Browse Parcels',
      description: 'Find parcels to carry on your trips',
      icon: FileText,
      link: '/parcels',
      color: 'bg-theme-warning'
    },
    {
      title: 'Browse Trips',
      description: 'Find travellers for your parcels',
      icon: MapPin,
      link: '/trips',
      color: 'bg-theme-error'
    },
  ];

  const getVerificationBadge = () => {
    if (!dashboardData?.user) return null;
    
    const badges = {
      unverified: { color: 'bg-theme-error-100 text-theme-error-800 border-theme-error-200', text: 'Unverified', action: 'Complete Verification' },
      partial: { color: 'bg-theme-warning-100 text-theme-warning-800 border-theme-warning-200', text: 'Pending Review', action: 'Verification Pending' },
      verified: { color: 'bg-theme-success-100 text-theme-success-800 border-theme-success-200', text: 'Verified', action: 'Verified' },
    };

    const badge = badges[dashboardData.user.verification_status] || badges.unverified;
    
    return (
      <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 bg-gradient-to-r from-theme-warning via-theme-primary to-theme-success" />
        
        {/* Content */}
        <div className="p-6">
          <h3 className="text-lg font-semibold text-theme-primary mb-4">Identity Verification</h3>
          <div className="flex items-center justify-between">
            <div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.color}`}>
                {badge.text}
              </span>
              <p className="text-sm text-theme-secondary mt-2">
                {dashboardData.user.verification_status === 'unverified' 
                  ? 'Complete verification to access all features'
                  : dashboardData.user.verification_status === 'partial'
                  ? 'Your documents are under review'
                  : 'Your identity is verified'
                }
              </p>
            </div>
            {dashboardData.user.verification_status === 'unverified' && (
              <Link
                to="/verification"
                className="inline-flex items-center px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover transition-colors"
              >
                Verify Now
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-theme-background">
      <SEO 
        title="Dashboard" 
        description="Overview of your delivery activities, agreements, and recent transactions on TripTrot"
        canonical="/dashboard"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Dashboard",
          "description": "User dashboard for parcel delivery platform"
        }}
      />
      
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-theme-primary">
              Welcome back, {dashboardData?.user?.first_name || 'User'}!
            </h1>
            <p className="mt-2 text-theme-secondary">
              Here's what's happening with your parcel delivery activities.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <StatCard key={index} {...stat} />
            ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-bold text-theme-primary mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickActions.map((action, index) => (
              <QuickAction key={index} {...action} />
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Wallet Balance */}
          <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-theme-success via-theme-primary to-theme-info" />
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Wallet Balance</h3>
                <Wallet className="h-5 w-5 text-theme-primary" />
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-theme-secondary">Available:</span>
                  <span className="font-medium text-theme-primary">
                    ${dashboardData?.stats?.available_balance || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-theme-secondary">Locked:</span>
                  <span className="font-medium text-theme-primary">
                    ${dashboardData?.stats?.locked_balance || '0.00'}
                  </span>
                </div>
                <div className="pt-3 border-t border-theme-light">
                  <div className="flex justify-between">
                    <span className="text-sm font-semibold text-theme-primary">Total:</span>
                    <span className="font-bold text-lg text-theme-primary">
                      ${(parseFloat(dashboardData?.stats?.available_balance || 0) + parseFloat(dashboardData?.stats?.locked_balance || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <Link
                to="/wallet"
                className="mt-4 block w-full text-center px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover transition-colors"
              >
                Manage Wallet
              </Link>
            </div>
          </div>

          {/* Reputation Score */}
          <div className="bg-theme-card border border-theme-light rounded-2xl hover:border-theme-primary/30 hover:shadow-xl transition-all duration-300 overflow-hidden">
            {/* Top accent bar */}
            <div className="h-1 bg-gradient-to-r from-theme-warning via-theme-primary to-theme-success" />
            
            {/* Content */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Reputation Score</h3>
                <TrendingUp className="h-5 w-5 text-theme-primary" />
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-theme-primary">
                  {dashboardData?.user?.reputation_score || 0}
                </div>
                <p className="text-sm text-theme-secondary mt-2">
                  {dashboardData?.user?.reputation_score >= 80 
                    ? 'Excellent reputation'
                    : dashboardData?.user?.reputation_score >= 60
                    ? 'Good reputation'
                    : dashboardData?.user?.reputation_score >= 40
                    ? 'Average reputation'
                    : 'Build your reputation'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          {/* {getVerificationBadge()} */}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
